import useGlobalFlag from 'hooks/useGlobalFlag'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { US_RESTRICTED_DATA_ENFORCEMENT_FLAG_KEY } from 'enrollment-platform/rbac/us-restricted-data-enforcement-flag'
import { useUsRestrictedDataAccess } from 'enrollment-platform/rbac/useUsRestrictedDataAccess'

/**
 * Whether the current user may download invoice files from Invoice Management:
 * the period ZIP, "Download Selected", and the file-name links on rows, version
 * history and the drawer. Mirrors EPS bulk-download, which needs Download on
 * InvoiceManagement and, since the files carry employee-level PHI (EMS-10939),
 * Download on UsRestrictedData. `Manage` is the action wildcard, so the
 * US-Restricted Data Access role qualifies.
 *
 * UI ergonomics only — the server is the real boundary. The UsRestrictedData
 * half applies only while the EPS GLOBAL flag
 * `service.eps.us-restricted-data.enable-enforcement` is on, matching
 * `UsRestrictedGuard`, which is a no-op while it is off. False while the flag
 * or either ability is loading, so the controls never flash in for a user who
 * turns out not to have it.
 */
export function useCanDownloadFromInvoiceManagement(): boolean {
  const invoiceManagement = usePermission(
    RbacActions.Download,
    RbacSubjects.InvoiceManagement
  )
  const { isEnabled: isUsDataEnforced, isLoading: isFlagLoading } =
    useGlobalFlag(US_RESTRICTED_DATA_ENFORCEMENT_FLAG_KEY)
  const { hasUsRestrictedDataAccess } = useUsRestrictedDataAccess(
    RbacActions.Download
  )
  if (isFlagLoading || invoiceManagement.isLoading) return false
  return (
    invoiceManagement.allowed &&
    (!isUsDataEnforced || hasUsRestrictedDataAccess)
  )
}
